import type { ComponentProps, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { controlClass, useFieldControl } from './fieldContext';

export interface InputProps extends Omit<ComponentProps<'input'>, 'prefix' | 'width'> {
  /** Text or icon shown inside the left edge (e.g. "£"). Decorative: put units in the label too. */
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Width hint for short inputs (e.g. codes, years). */
  width?: 'full' | 'xs' | 'sm' | 'md';
}

const widths = { full: 'w-full', xs: 'max-w-[7ch]', sm: 'max-w-[12ch]', md: 'max-w-[24ch]' };

export function Input({ prefix, suffix, width = 'full', className, ...props }: InputProps) {
  const p = useFieldControl(props);
  if (!prefix && !suffix) {
    return <input className={cn(controlClass, widths[width], className)} {...p} />;
  }
  return (
    <div className={cn('relative flex items-stretch', widths[width])}>
      {prefix && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 font-bold text-muted"
        >
          {prefix}
        </span>
      )}
      <input className={cn(controlClass, prefix && 'pl-8', suffix && 'pr-12', className)} {...p} />
      {suffix && <span className="absolute inset-y-0 right-0 flex items-center pr-1">{suffix}</span>}
    </div>
  );
}

export function Textarea({ className, rows = 4, ...props }: ComponentProps<'textarea'>) {
  const p = useFieldControl(props);
  return <textarea rows={rows} className={cn(controlClass, 'min-h-24 leading-relaxed', className)} {...p} />;
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends ComponentProps<'select'> {
  options?: SelectOption[];
  /** Adds a first empty option, e.g. "Choose a category". */
  placeholder?: string;
}

/** Native select (best for screen readers and mobile pickers). */
export function Select({ options, placeholder, className, children, ...props }: SelectProps) {
  const p = useFieldControl(props);
  return (
    <div className="relative w-full">
      <select className={cn(controlClass, 'appearance-none pr-10', className)} {...p}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-muted"
      />
    </div>
  );
}
